export type APIResponse<T> = { success: true, data: T, pagination?: PaginationMetadata } | { success: false, error: string, errors: string[] }

interface PaginationMetadata {
  page: number
  pages: number
  itemsPerPage: number
  itemsTotal: number
}

export class ValidationError extends Error {
  public constructor(public message: string, public errors: string[]) {
    super('Validation error')
  }
}

export function parseItemResponse<T>(json: unknown, factory: ModelFactory<T>): APIResponse<T> {
  try {
    return { success: true, data: factory.fromItemResponse(json) };
  } catch (e: unknown) {
    return { success: false, error: (e instanceof Error) ? e.message : 'Unknown error', errors: (e instanceof ValidationError) ? e.errors : [] }
  }
}

export function parseItemsResponse<T>(json: unknown, factory: ModelFactory<T>): APIResponse<T[]> {
  try {
    return { success: true, data: factory.fromItemsResponse(json), pagination: parsePaginationMetadata(json) };
  } catch (e: unknown) {
    console.error(e)
    return { success: false, error: (e instanceof Error) ? e.message : 'Unknown error', errors: (e instanceof ValidationError) ? e.errors : [] };
  }
}

export abstract class ModelFactory<T> {
  protected abstract fromJson(json: any): T

  public fromItemResponse(json: unknown): T {
    if (typeof json !== 'object' || json === null || !('item' in json) || typeof json['item'] !== 'object') {
      if (typeof json === 'object' && json && 'message' in json && typeof json['message'] === 'string') {
        let errors: string[] = []
        if ('errors' in json && Array.isArray(json['errors'])) {
          errors = json['errors']
        }
        if ('errors' in json && typeof json['errors'] === 'object' && json['errors'] !== null) {
          errors = Object.values(json['errors'])
        }
        throw new ValidationError(json['message'], errors);
      }
      if (typeof json === "object" && json !== null && !('item' in json)) {
        return this.fromJson(json)
      }
      throw new Error('Invalid response from server.');
    }
    return this.fromJson(json['item']);
  }

  public fromItemsResponse(json: unknown): T[] {
    let items: unknown[]
    if (Array.isArray(json)) {
      items = json
    } else if (typeof json !== 'object' || json === null || !('items' in json) || !Array.isArray(json['items'])) {
      throw new Error('Invalid response from server.');
    } else {
      items = json['items']
    }
    return items.map(item => {
      if (typeof item !== 'object') {
        throw new Error('Invalid item in JSON response.')
      }
      return this.fromJson(item)
    })
  }
}

function parsePaginationMetadata(json: unknown): PaginationMetadata | undefined {
  if (typeof json !== 'object' || json === null || !('pagination' in json) || typeof json['pagination'] !== 'object') {
    return undefined
  }
  const pagination = json['pagination'] as { current_page: number, from: number, last_page: number, per_page: number, to: number, total: number }
  return {
    page: pagination.current_page,
    pages: pagination.last_page,
    itemsPerPage: pagination.per_page,
    itemsTotal: pagination.total,
  }
}
